import { useMemo, useCallback, useState } from 'react'
import { isValid } from 'date-fns'
import { DateRange } from '@mui/x-date-pickers-pro-7'
import { ERROR_MESSAGES } from './constants'
import { isRangeExceedsMax, isInvalidRange } from './utils'
import { formatDateWithLocale } from '../../../utils/date-utils'

export type DateRangeValidationError = [string | null, string | null] | null

interface ValidationResult {
  error: boolean
  errorType: string | null
  helperText: string
}

interface UseValidationProps {
  value: DateRange<Date>
  touched: boolean
  required?: boolean
  helperText?: string
  maxDate?: Date
  minDate?: Date
  maxRangeDays?: number
  fromDateError?: boolean
  toDateError?: boolean
}

interface FieldValidationParams {
  fieldError: string | null
  fieldType: 'start' | 'end'
  touched: boolean
  required?: boolean
  helperText?: string
  startDate: Date | null
  endDate: Date | null
  maxRangeDays?: number
  externalError?: boolean
  minDate?: Date
  maxDate?: Date
}

const createErrorMessage = (
  errorType: string, 
  fieldType: 'start' | 'end',
  maxDate?: Date,
  minDate?: Date
) => {
  const fieldName = fieldType === 'start' ? 'start' : 'end'
  
  switch (errorType) {
    case 'maxDate':
      return maxDate 
        ? `Please select a ${fieldName} date before ${formatDateWithLocale(maxDate)}`
        : ''
    case 'minDate':
      return minDate 
        ? `Please select a ${fieldName} date after ${formatDateWithLocale(minDate)}`
        : ''
    case 'invalidDate':
    case 'shouldDisableDate':
      return ERROR_MESSAGES.invalidDate(fieldType)
    case 'disableFuture':
      return ERROR_MESSAGES.disableFuture(fieldName)
    case 'disablePast':
      return ERROR_MESSAGES.disablePast(fieldName)
    default:
      return ''
  }
}

const validateDateRange = (
  startDate: Date | null,
  endDate: Date | null,
  fieldType: 'start' | 'end',
  maxRangeDays?: number
): ValidationResult | null => {
  const isStartField = fieldType === 'start'
  
  // Check for maximum range validation
  if (isRangeExceedsMax(startDate, endDate, maxRangeDays)) {
    return {
      error: true,
      errorType: 'exceedsMaxDateRange',
      helperText: isStartField ? ERROR_MESSAGES.exceedsMaxRange(maxRangeDays!) : ''
    }
  }
  
  // Check for invalid range (only show error on start field)
  if (isStartField && isInvalidRange(startDate, endDate)) {
    return {
      error: true,
      errorType: 'wrongOrder',
      helperText: ERROR_MESSAGES.wrongOrder
    }
  }
  
  return null
}

const validateFieldSpecificErrors = (
  fieldError: string | null,
  fieldType: 'start' | 'end',
  maxDate?: Date,
  minDate?: Date
): ValidationResult | null => {
  if (fieldError) {
    return {
      error: true,
      errorType: fieldError,
      helperText: createErrorMessage(fieldError, fieldType, maxDate, minDate)
    }
  }
  
  return null
}

const validateRequiredField = (
  date: Date | null,
  touched: boolean,
  required?: boolean
): ValidationResult | null => {
  if (touched && required && !isValid(date)) {
    return {
      error: true,
      errorType: 'missingRequired',
      helperText: ERROR_MESSAGES.required
    }
  }
  
  return null
}

const validateExternalError = (
  externalError?: boolean
): ValidationResult | null => {
  if (externalError) {
    return {
      error: true,
      errorType: 'external',
      helperText: ''
    }
  }
  
  return null
}

// Generic field validation function
const validateField = (params: FieldValidationParams): ValidationResult => {
  const {
    fieldError,
    fieldType,
    touched,
    required,
    helperText,
    startDate,
    endDate,
    maxRangeDays,
    externalError,
    minDate,
    maxDate
  } = params

  // Derive the current field's date from fieldType
  const date = fieldType === 'start' ? startDate : endDate

  // Range validations (order matters - range checks first)
  const rangeError = validateDateRange(startDate, endDate, fieldType, maxRangeDays)
  if (rangeError) return rangeError

  // Field-specific errors
  const fieldSpecificError = validateFieldSpecificErrors(fieldError, fieldType, maxDate, minDate)
  if (fieldSpecificError) return fieldSpecificError

  // Required validation
  const requiredError = validateRequiredField(date, touched, required)
  if (requiredError) return requiredError

  // External error
  const externalErr = validateExternalError(externalError)
  if (externalErr) return externalErr

  // No errors - return success state
  return {
    error: false,
    errorType: null,
    helperText: helperText || (required && 'Required') || ''
  }
}

export const useValidation = ({
  value,
  touched,
  required,
  helperText,
  maxDate,
  minDate,
  maxRangeDays,
  fromDateError,
  toDateError
}: UseValidationProps) => {
  const [startDate, endDate] = value
  const [internalError, setInternalError] = useState<DateRangeValidationError>(null)

  const handleError = useCallback((error: DateRangeValidationError) => {
    setInternalError(error)
  }, [])

  const startFieldValidation = useMemo<ValidationResult>(() => {
    const [startError] = internalError || [null, null]
    
    return validateField({
      fieldError: startError,
      fieldType: 'start',
      touched,
      required,
      helperText,
      startDate,
      endDate,
      maxRangeDays,
      externalError: fromDateError,
      minDate,
      maxDate
    })
  }, [
    startDate,
    endDate,
    internalError,
    touched,
    required,
    maxRangeDays,
    helperText,
    fromDateError,
    minDate,
    maxDate
  ])

  const endFieldValidation = useMemo<ValidationResult>(() => {
    const [, endError] = internalError || [null, null]
    
    return validateField({
      fieldError: endError,
      fieldType: 'end',
      touched,
      required,
      helperText,
      startDate,
      endDate,
      maxRangeDays,
      externalError: toDateError,
      minDate,
      maxDate
    })
  }, [
    startDate,
    endDate,
    internalError,
    touched,
    required,
    helperText,
    maxRangeDays,
    toDateError,
    minDate,
    maxDate
  ])

  return {
    startFieldValidation,
    endFieldValidation,
    onError: handleError
  }
}